import {
  useAcceptMemory,
  useRevertMemory,
  type MemoryActionPermission,
  type MemoryFeedbackTarget,
} from "@/features/material-compliance/api/memoryApi"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Check, ThumbsDown, ThumbsUp } from "lucide-react"
import { useState } from "react"

export interface MemoryRatingProps {
  target: MemoryFeedbackTarget
  permission: MemoryActionPermission
  testId: string
  /** The value this rating is about, e.g. "0.05 %" — only needed where more than one
   *  rating cluster can appear together (a conflict's two sides), so the two thumbs-up
   *  read as "Helpful: 0.05 %" / "Helpful: 0.08 %" rather than both just "Helpful".
   *  Mirrors the same fix already applied to `MC_MEMORY_CONFLICT_SELECT`. */
  subjectLabel?: string
}

/**
 * Was this memory any good? Two buttons, and neither one writes the field.
 *
 * WHAT CHANGED AND WHY IT MATTERS. These replace "Use this" and "Not right". The old pair
 * conflated two unrelated questions — "put this value in the box" and "is this memory
 * correct" — into one click, which meant memory could only learn from someone who happened
 * to want the value right then. A reviewer who read a suggestion, agreed with it, and typed
 * the number themselves taught the system nothing. Separating them makes the suggestion
 * purely advisory and the rating purely a rating.
 *
 * THIS IS STILL THE MECHANISM BEHIND SCENARIO #7. A thumbs-down is a revert: it counts, it
 * lowers that entry's confidence, and three of them retire the entry so it stops being
 * offered. That is the only way a wrong memory dies, so removing the gesture entirely would
 * have left a bad correction haunting the org forever.
 *
 * ON A CONFLICTED ENTRY it still counts and still moves confidence, but it does NOT retire
 * the entry — `_demote` refuses a conflicted status on purpose, because retiring one side
 * of a contradiction is deciding it, and the system never decides. The flag clears when a
 * person's pick lands as its own decision, which does not exist yet.
 *
 * PENDING, THEN CONFIRMED — NOT LATCHED ON FIRST CLICK. A thumb only selects a PENDING
 * choice: freely changeable (click it again to clear, click the other thumb to switch)
 * until a Confirm appears and is pressed. Only Confirm fires the mutation. This exists
 * because the previous version fired on the very first click and then disabled both
 * buttons — an accidental tap was permanent, with no way to undo it.
 *
 * THREE STATES, READABLE WITHOUT COLOUR: untouched (grey, no Confirm) → pending (the chosen
 * thumb highlighted, the OTHER one dimmed but still clickable so a switch is one click, and
 * Confirm appears — its presence is itself the signal that something is pending) →
 * committed (filled, both thumbs disabled, Confirm gone). `aria-pressed` reads
 * `(committed ?? pending) === direction` so an assistive technology sees the same three
 * states a sighted reviewer does.
 *
 * LATCHED OPTIMISTICALLY, IN THE CONFIRM HANDLER ITSELF — not in the mutation's `onSuccess`.
 * That makes firing the mutation twice structurally impossible (the state that gates the
 * handler is set in the same breath as the call) and keeps the existing fire-and-forget
 * doctrine: a rating that fails to record is a lost measurement, never an error blocking the
 * reviewer. A permission denial still fails silently, the same way it always has.
 *
 * A CONFIRM BUTTON ALREADY EXISTS ON A CONFLICT CARD — "This is correct"
 * (`MC_MEMORY_CONFIRM_CORRECT`), which RESOLVES THE CONTRADICTION, a completely different
 * action from "record my thumb." Putting a second, differently-meaning Confirm in the same
 * card is a real collision risk, so this one is deliberately small and silent: icon-only
 * (no competing label text), its own `data-testid="memory-rating-confirm-${testId}"`, and
 * its own translation key `MC_MEMORY_RATING_CONFIRM` — never `MC_MEMORY_CONFIRM_CORRECT`.
 *
 * REMOUNT IS A KNOWN SEAM, NOT FIXED HERE. A committed rating is invisible again after this
 * component remounts, because nothing persists it client-side and the hint payload does not
 * yet carry a prior vote. No client-side store (localStorage included) could be honest about
 * this anyway — a vote belongs to a person, not a browser, and the same person can rate from
 * a different device. The real fix is the server returning `viewerRating` on the hint; until
 * then, re-rating after a remount simply hits the backend's own repeat guard, which already
 * ignores a second identical vote without double-counting it.
 */
export function MemoryRating({
  target,
  permission,
  testId,
  subjectLabel,
}: MemoryRatingProps) {
  const { translate } = useTranslation()
  const accept = useAcceptMemory(permission)
  const revert = useRevertMemory(permission)
  const [rating, setRating] = useState<{
    targetKey: string
    pending: "up" | "down" | null
    committed: "up" | "down" | null
  } | null>(null)
  const targetKey = JSON.stringify([
    target.entryId,
    target.surface,
    target.targetType ?? null,
    target.targetId ?? null,
  ])
  // A recycled instance must not read `pending` from a new entry against `committed` left
  // over from the old one — one object, scoped by the same key together, makes that
  // impossible rather than merely unlikely.
  const current = rating?.targetKey === targetKey ? rating : null
  const pending = current?.pending ?? null
  const committed = current?.committed ?? null

  const choose = (direction: "up" | "down") => {
    if (committed) return
    setRating({
      targetKey,
      pending: pending === direction ? null : direction,
      committed: null,
    })
  }

  const confirm = () => {
    if (committed || !pending) return
    const direction = pending
    // Latched in this same handler, not in the mutation's `onSuccess` — see the module
    // docstring. That is what makes a double-fire structurally impossible.
    setRating({ targetKey, pending: null, committed: direction })
    if (direction === "up") accept.mutate(target)
    else revert.mutate(target)
  }

  const helpfulLabel = subjectLabel
    ? `${translate("MC_MEMORY_HELPFUL")}: ${subjectLabel}`
    : translate("MC_MEMORY_HELPFUL")
  const notHelpfulLabel = subjectLabel
    ? `${translate("MC_MEMORY_NOT_HELPFUL")}: ${subjectLabel}`
    : translate("MC_MEMORY_NOT_HELPFUL")

  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        aria-label={helpfulLabel}
        aria-pressed={(committed ?? pending) === "up"}
        disabled={committed !== null}
        onClick={() => choose("up")}
        className={`rounded p-1 transition-colors disabled:cursor-default ${
          (committed ?? pending) === "up"
            ? "text-attention"
            : "text-grey5 hover:text-white"
        } ${(committed ?? pending) === "down" ? "opacity-40" : ""}`}
        data-testid={`memory-thumbs-up-${testId}`}
      >
        <ThumbsUp size={12} aria-hidden="true" />
      </button>
      <button
        type="button"
        aria-label={notHelpfulLabel}
        aria-pressed={(committed ?? pending) === "down"}
        disabled={committed !== null}
        onClick={() => choose("down")}
        className={`rounded p-1 transition-colors disabled:cursor-default ${
          (committed ?? pending) === "down"
            ? "text-attention"
            : "text-grey5 hover:text-white"
        } ${(committed ?? pending) === "up" ? "opacity-40" : ""}`}
        data-testid={`memory-thumbs-down-${testId}`}
      >
        <ThumbsDown size={12} aria-hidden="true" />
      </button>
      {pending && !committed ? (
        <button
          type="button"
          aria-label={translate("MC_MEMORY_RATING_CONFIRM")}
          onClick={confirm}
          className="rounded p-1 text-attention transition-colors hover:text-white"
          data-testid={`memory-rating-confirm-${testId}`}
        >
          <Check size={12} aria-hidden="true" />
        </button>
      ) : null}
    </div>
  )
}
